import React, {useState} from 'react';
import {useForm} from 'react-hook-form';
import cn from 'classnames';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroup from 'shared/ui/FormGroup/FormGroup';
import Select from 'shared/ui/Select/Select';
import TextArea from 'shared/ui/TextArea/TextArea';
import ArrowForCheckbox from 'shared/ui/icons/ArrowForCheckbox';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';

import {isTranslate} from 'shared/lib/lang';

import {ISupportRequest} from '../../../model/ISupport';

import StarRating from '../StarRating/StarRating';

import {supportAPI} from '../../../api/supportAPI';

import styles from './SupportForm.module.scss';

interface ISupportFormProps {
    onClose: () => void;
}

const SupportForm: React.FC<ISupportFormProps> = ({onClose}) => {
    const [isVisible, setIsVisible] = useState(false);
    const {register, control, handleSubmit, watch, formState: {errors}, setValue} = useForm<ISupportRequest>({
        defaultValues: {
            topic: t`Ошибка в работе сервиса`,
            message: '',
            rating: 0,
            likesDislikes: '',
            featureRequest: '',
        },
        mode: 'onChange',
    });

    const selectedTopic = watch('topic');
    const rating = watch('rating') || 0;

    const toggleVisibility = () => {
        setIsVisible(!isVisible);
    };

    const getPlaceholder = (topicValue: string) => {
        switch(topicValue) {
            case '1':
                return t`Расскажите, где произошла ошибка и что произошло вместо ожидаемого результата`;
            case '2':
                return t`Предложите изменение или новую возможность, которая улучшит работу сервиса`;
            case '3':
                return t`Опишите вашу задачу или проблему`;
            case '4':
                return t`Опишите, что именно в работе сервиса осталось непонятным – мы поможем разобраться`;
            case '5':
                return t`Напишите, что хотите уточнить по оплате или тарифам сервиса`;
            case '6':
                return t`Расскажите, что именно хотите сообщить – любая дополнительная информация важна`;
            default:
                return t`Расскажите, где произошла ошибка и что произошло вместо ожидаемого результата`;
        }
    };

    const options = [
        {
            label: t`Ошибка в работе сервиса`,
            value: t`Ошибка в работе сервиса`,
        },
        {
            label: t`Предложение улучшения`,
            value: t`Предложение улучшения`,
        },
        {
            label: t`Нужна поддержка сайта`,
            value: t`Нужна поддержка сайта`,
        },
        {
            label: t`Вопрос по функционалу`,
            value: t`Вопрос по функционалу`,
        },
        {
            label: t`Вопрос по оплате или тарифам`,
            value: 'Вопрос по оплате или тарифам',
        },
        {
            label: t`Другое`,
            value: t`Другое`,
        },
    ];

    const handleRatingChange = (newRating: number) => {
        setValue('rating', newRating);
        if (newRating > 0 && !isVisible) {
            setIsVisible(true);
        }
    };

    const [addMessage, { isLoading }] = supportAPI.useAddSupportMessageMutation();

    const onSubmit = async(data: ISupportRequest) => {
        try {
            await addMessage(data).unwrap();
            toast.info(t`Форма успешно отправлена`);
            onClose();
        } catch (error) {
            toast.info(t`Ошибка отправки формы`);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBodyWrapper>
                <FormGroup className={styles.formGroup}>
                    <label className={styles.label}>
                        {t`Тема обращения`}
                    </label>
                    <Select
                        control={control}
                        name="topic"
                        data={options}
                        searchable={false}
                    />
                    <TextArea
                        className={styles.textAria}
                        placeholder={getPlaceholder(selectedTopic)}
                        register={register}
                        name="message"
                        errorMsg={t`Введите текст обращения`}
                    />
                    {errors.message && (
                        <span className={styles.errorText}>
                            {errors.message.message}
                        </span>
                    )}
                </FormGroup>
                <div className={styles.reviewBlock}>
                    <label className={styles.label}>
                        {t`Нам важна ваша оценка`}
                    </label>
                    <StarRating
                        onChange={handleRatingChange}
                        defaultValue={rating}
                    />
                    <span
                        className={cn(
                            styles.icon,
                            isTranslate() && styles.enIcon,
                            isVisible && styles.iconVisible,
                        )}
                        onClick={toggleVisibility}
                    >
                        <ArrowForCheckbox />
                    </span>
                </div>
                <div className={cn(styles.additionalBlock, isVisible && styles.show)}>
                    <span className={styles.line} />
                    <FormGroup className={styles.formGroup}>
                        <span className={styles.title}>
                            {t`Что в сервисе вам нравится, а что вызывает сложности или неудобства?`}
                        </span>
                        <TextArea
                            className={styles.textAria}
                            placeholder={t`Опишите, что в работе сервиса вам кажется удачным, а что стоит улучшить`}
                            register={register}
                            name="likesDislikes"
                        />
                    </FormGroup>
                    <FormGroup className={styles.formGroup}>
                        <span className={styles.title}>
                            {t`Какие функции вы хотели бы добавить или улучшить?`}
                        </span>
                        <TextArea
                            className={styles.textAria}
                            /* eslint-disable-next-line max-len */
                            placeholder={t`Опишите функцию или возможность, которой вам не хватает, мы рассмотрим ваше предложение`}
                            register={register}
                            name="featureRequest"
                        />
                    </FormGroup>
                </div>
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={isLoading ? t`Отправка...` : t`Отправить`}
                primaryButtonType="submit"
                primaryButtonLoading={isLoading}
                primaryButtonDisabled={isLoading || !watch('message')?.trim()}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default SupportForm;